/** @jsx isolateComponent */

import type { ReactNode } from 'react';
import type { Interpolation } from '@emotion/core';

import { TooltipWrapper } from '../../display/TooltipWrapper';
import { kebabCaseAriaAttrs } from '../../../utils/formatAriaAttrKeys';
import { Animation, Colors, Shadows, Typography } from '../../../theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

type Size = 'small' | 'medium' | 'large';

type Props = {
  /**
   * Optional custom `aria-label` for the component. By default
   * `aria-label` will be computed based on the `checked` and
   * `indeterminate` props on this component and their corresponding
   * `*Text` props. However because that causes the `aria-label` to
   * change as `ToggleSwitch` is un/checked, that can be confusing
   * for screenreader users in some scenarios. In that case, use this
   * `ariaLabel` prop to clear up any ambiguity.
   */
  ariaLabel?: string;
  /**
   * Indicates whether the switch is checked on or off.
   */
  checked?: boolean;
  /**
   * Text to render when the switch is checked on.
   */
  checkedText: string;
  /**
   * Indicates whether the switch is disabled.
   */
  disabled?: boolean;
  /**
   * HTML id of the input
   */
  id?: string;
  /**
   * Indicates whether the switch is neither checked on nor off.
   */
  indeterminate?: boolean;
  /**
   * Text to render when the switch is neither checked on nor off.
   */
  indeterminateText: string;
  /**
   * HTML name attribute, typically used for the key that will
   * be sent to the API.
   */
  name?: string;
  /**
   * The size of the `ToggleSwitch`.
   */
  size: Size;
  /**
   * Handler invoked when the switch changes from checked on or off.
   */
  onChange: React.ChangeEventHandler;
  /**
   * Text to render when the switch is unchecked.
   */
  uncheckedText: string;
  /**
   * HTML value attribute, i.e. the actual value of the input.
   */
  value?: string;
};

const dotSizes = {
  small: 14,
  medium: 20,
  large: 24,
};

const Styles = labelStyles('ToggleSwitch', {
  root: (props: Props) => [
    {
      ...Typography.allCaps900,
      color: Colors.white,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'stretch',
      overflow: 'hidden',
      transition: `all ${Animation.transitionDuration} ease-in-out`,

      '&:focus-within:not(:hover)': {
        boxShadow: `0 0 0 2px ${Colors.blue500}`,
      },
    },
    props.size === 'small' &&
      withModifier(props.size, {
        width: 40,
        height: 20,
        borderRadius: 20,
        fontSize: 0,
        lineHeight: 0,
        padding: 3,
      }),
    props.size === 'medium' &&
      withModifier(props.size, {
        minWidth: 60,
        height: 30,
        borderRadius: 30,
        fontSize: 10,
        lineHeight: '15px',
        padding: 5,
      }),
    props.size === 'large' &&
      withModifier(props.size, {
        minWidth: 80,
        height: 40,
        borderRadius: 40,
        fontSize: 14,
        lineHeight: '20px',
        padding: 8,
      }),
    !props.checked &&
      withModifier('unchecked', {
        backgroundColor: Colors.grey700,
      }),
    props.checked &&
      withModifier('checked', {
        backgroundColor: Colors.green400,
      }),
    props.indeterminate &&
      withModifier('indeterminate', {
        backgroundColor: Colors.orange600,
      }),
    props.disabled &&
      withModifier('disabled', {
        cursor: 'not-allowed',
        opacity: 0.5,
      }),
  ],

  innerWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },

  // Still needs to be "visible" so it can be tabbed to,
  // but it shouldn't render anything. Take it out of layout
  // flow and hide it with `opacity` for good measure. Also
  // stretch it across the container so tools like VoiceOver
  // show an outline in the correct location.
  input: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    zIndex: -1,
  },

  // This node's text should be centered within the available space
  // outside of the dot. For instance when the dot is on the left,
  // the text will be to the right of the dot, but centered in that
  // available space. When the dot is on the right, the text will be
  // to the left of the dot, but centered in that available space.
  text: (props: Props) => [
    {
      display: 'block',
      width: '100%',
      userSelect: 'none',
      textAlign: 'center',
    },
    !props.checked &&
      withModifier(`unchecked-${props.size}`, {
        paddingLeft: `calc(${dotSizes[props.size]}px + 3px)`,
        paddingRight: 0,
      }),
    props.checked &&
      withModifier(`checked-${props.size}`, {
        paddingLeft: 0,
        paddingRight: `calc(${dotSizes[props.size]}px + 3px)`,
      }),
    props.indeterminate &&
      withModifier('indeterminate', {
        paddingLeft: 0,
        paddingRight: 0,
      }),
  ],

  // Render all text, stacked on top of each other, and then visibly hide
  // the `isHidden` text. This ensures that `ToggleSwitch` is as wide as
  // its longest text label, even if that label is hidden.
  textInner: ({ isHidden }: { isHidden: boolean }) => [
    {
      display: 'block',
    },
    isHidden &&
      withModifier('hidden', {
        visibility: 'hidden',
        height: 0,
      }),
  ],

  dot: (props: Props) => [
    {
      display: 'block',
      borderRadius: '100%',
      backgroundColor: Colors.white,
      position: 'absolute',
      top: 0,
      transition: `all ${Animation.transitionDuration} ease-in-out`,
      boxShadow: Shadows.elevation5,
    },
    !props.checked &&
      withModifier('unchecked', {
        left: 0,
        transform: `translateX(0)`,
      }),
    props.checked &&
      // Using `left` and `transform` for proper `transition`.
      // Using `right: auto` for instance won't transition properly.
      // `translate` is used over `calc` since `translate`'s argument
      // is the size of the `dot` itself rather than its parent.
      withModifier('checked', {
        left: '100%',
        transform: `translateX(-100%)`,
      }),
    withModifier(props.size, {
      width: dotSizes[props.size],
      height: dotSizes[props.size],
    }),
  ],
});

const InnerText = ({
  children,
  isHidden,
}: {
  children: ReactNode;
  isHidden: boolean;
}) => {
  return (
    <span
      aria-hidden="true"
      css={Styles.textInner({ isHidden }) as Interpolation}
    >
      {children}
    </span>
  );
};

const getAriaLabel = (props: Props) => {
  if (props.ariaLabel) {
    return props.ariaLabel;
  }
  if (props.indeterminate) {
    return props.indeterminateText;
  }
  return props.checked ? props.checkedText : props.uncheckedText;
};

/**
 * `ToggleSwitch` is a stylized checkbox used to indicate whether something is on or off.
 */
export const ToggleSwitch = (props: Props) => {
  return (
    <TooltipWrapper content={props.disabled ? 'Disabled' : ''}>
      {({ childProps }) => (
        <label
          {...kebabCaseAriaAttrs(childProps)}
          css={Styles.root(props) as Interpolation}
        >
          <span css={Styles.innerWrap as Interpolation}>
            <input
              // Not all screenreaders announce the `disabled` attribute,
              // so add `aria-disabled` to reinforce.
              aria-disabled={props.disabled}
              aria-label={getAriaLabel(props)}
              checked={props.checked}
              css={Styles.input as Interpolation}
              disabled={props.disabled}
              id={props.id}
              name={props.name}
              onChange={props.onChange}
              type="checkbox"
              value={props.value}
            />
            <span css={Styles.text(props) as Interpolation}>
              {/* See note at `Styles.textInner` for why all texts are rendered. */}
              <InnerText isHidden={props.indeterminate || !!props.checked}>
                {props.uncheckedText}
              </InnerText>
              <InnerText isHidden={props.indeterminate || !props.checked}>
                {props.checkedText}
              </InnerText>
              <InnerText isHidden={!props.indeterminate}>
                {props.indeterminateText}
              </InnerText>
            </span>
            {!props.indeterminate && (
              <span
                aria-hidden="true"
                css={Styles.dot(props) as Interpolation}
                role="presentation"
              />
            )}
          </span>
        </label>
      )}
    </TooltipWrapper>
  );
};

ToggleSwitch.defaultProps = {
  checkedText: 'On',
  indeterminateText: 'N/A',
  onChange: () => {},
  size: 'medium',
  uncheckedText: 'Off',
};
